/**
 * Rules profiles — the places where two teachers of the same law disagree.
 *
 * Everything not named here (the consonance table, parallels, direct perfects,
 * runs of imperfect consonances, melodic hygiene) is shared, and lives in
 * `analysis.ts`. See DESIGN.md for the discussion behind each divergence.
 */

import type { RulesProfile } from './types';

export interface RulesSpec {
  readonly id: RulesProfile;
  readonly label: string;
  /** One line for the settings sheet. */
  readonly blurb: string;
  /**
   * Diatonic distances the counterpoint may open on when it is the **upper**
   * voice (the cantus firmus below). Lim wants a perfect consonance; Gran
   * welcomes a 3rd or 10th as well.
   */
  readonly openingAbove: readonly number[];
  /** Distances the counterpoint may open on when it is the lower voice. */
  readonly openingBelow: readonly number[];
  /** Whether a repeated note in the counterpoint is an error or a mild warning. */
  readonly repeatedNote: 'forbidden' | 'sparing';
  /** Gran bars any two perfect consonances in a row, not only parallels. */
  readonly barTwoPerfects: boolean;
  /** Widest diatonic spacing tolerated between the voices, or `null` for no limit. */
  readonly maxSpacing: number | null;
  /** Gran asks the two lines not to peak in the same measure. */
  readonly offsetClimax: boolean;
}

const LIM: RulesSpec = {
  id: 'lim',
  label: 'Lim',
  blurb: 'Perfect consonance to open; no repeated notes; voices free to spread.',
  openingAbove: [0, 4, 7, 11],
  openingBelow: [0, 7],
  repeatedNote: 'forbidden',
  barTwoPerfects: false,
  maxSpacing: null,
  offsetClimax: false,
};

const GRAN: RulesSpec = {
  id: 'gran',
  label: 'Gran',
  blurb: 'Opens on degree 1, 3 or 5; a repeated tone is allowed sparingly; voices stay within a 10th.',
  openingAbove: [0, 2, 4, 7, 9],
  openingBelow: [0, 7],
  repeatedNote: 'sparing',
  barTwoPerfects: true,
  maxSpacing: 9,
  offsetClimax: true,
};

/** Both profiles, in menu order. */
export const RULES: readonly RulesSpec[] = [LIM, GRAN];

/** Resolve a profile id, defaulting to Lim. */
export const rulesFor = (id: RulesProfile | undefined): RulesSpec =>
  id === 'gran' ? GRAN : LIM;
